import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  ShieldOff,
  ShieldCheck,
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  BarChart2,
  Clock,
  MessageCircle,
  CheckCircle2,
  AlertTriangle,
  CalendarPlus,
  Repeat,
  Save,
  Bot,
} from 'lucide-react'
import { shopsApi, masterDataApi, corePaymentsApi, plansApi, subscriptionsApi, type CorePayment } from '../../lib/api'
import StatusBadge from '../../components/shared/StatusBadge'
import PlanBadge from '../../components/shared/PlanBadge'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import DataTable, { Column } from '../../components/shared/DataTable'
import LoadingState from '../../components/shared/LoadingState'
import ErrorState from '../../components/shared/ErrorState'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDate, formatDatetime, formatMoney, formatRelativeTime } from '../../lib/utils'
import type { BillingInvoice, AuditLog, NotificationLog, SupportTicket, ShopUser, ShopBranch, Shop } from '../../types'
import { tx } from '../../lib/i18n'

const TABS = [
  'Overview',
  'Subscription',
  'Users',
  'Branches',
  'Payments',
  'Usage',
  'Billing Logs',
  'Notifications',
  'Support Tickets',
  'Audit Logs',
] as const
type Tab = (typeof TABS)[number]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorOf = (err: any, fallback: string): string => err?.response?.data?.error ?? fallback

const inputCls =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'

const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', manager: 'Manager', staff: 'Staff', user: 'User' }

const InfoRow: React.FC<{ label: string; value: React.ReactNode; icon?: React.ElementType }> = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
    {Icon && (
      <div className="p-1.5 bg-gray-100 rounded-lg flex-shrink-0 mt-0.5">
        <Icon className="h-3.5 w-3.5 text-gray-500" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <div className="mt-0.5 text-sm text-gray-800 font-medium">{value ?? '—'}</div>
    </div>
  </div>
)

const AI_ADDON_NAMES: Record<string, string> = { ai_growth: 'AI Growth', ai_crm: 'AI CRM', ai_full: 'AI Full Suite' }

const ShopDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [activeTab, setActiveTab] = useState<Tab>('Overview')
  const [subAction, setSubAction] = useState<null | 'extend' | 'plan'>(null)
  const [extendDays, setExtendDays] = useState(30)
  const [newPlanId, setNewPlanId] = useState('')
  const [subReason, setSubReason] = useState('')
  const [suspendDialog, setSuspendDialog] = useState(false)
  const [reactivateDialog, setReactivateDialog] = useState(false)
  const [reason, setReason] = useState('')

  const [form, setForm] = useState({
    businessTypeId: '',
    logoUrl: '',
    timezoneOverride: '',
    internalNote: '',
  })

  const { data: businessTypesData } = useQuery({
    queryKey: ['business-types'],
    queryFn: () => masterDataApi.listBusinessTypes().then((r) => r.data.data ?? []),
  })

  const { data: shopData, isLoading, error, refetch } = useQuery({
    queryKey: ['shop', id],
    queryFn: () => shopsApi.get(id!).then((r) => r.data.data),
    enabled: !!id,
  })

  const { data: usageData } = useQuery({
    queryKey: ['shop-usage', id],
    queryFn: () => shopsApi.getUsage(id!).then((r) => r.data.data),
    enabled: !!id,
  })

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['shop-users', id],
    queryFn: () => shopsApi.getUsers(id!).then((r) => r.data.data ?? []),
    enabled: !!id && (activeTab === 'Users' || activeTab === 'Overview'),
  })

  const { data: branchesData, isLoading: branchesLoading } = useQuery({
    queryKey: ['shop-branches', id],
    queryFn: () => shopsApi.getBranches(id!).then((r) => r.data.data ?? []),
    enabled: !!id && activeTab === 'Branches',
  })

  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansApi.list().then((r) => r.data.data ?? []),
    enabled: activeTab === 'Subscription',
    staleTime: 5 * 60_000,
  })

  const { data: invoicesData } = useQuery({
    queryKey: ['shop-invoices', id],
    queryFn: () => shopsApi.getInvoices(id!).then((r) => r.data),
    enabled: !!id && activeTab === 'Billing Logs',
  })

  const { data: paymentsData } = useQuery({
    queryKey: ['shop-payments', id],
    queryFn: () => corePaymentsApi.list({ shopId: id!, limit: 50 }).then((r) => r.data),
    enabled: !!id && activeTab === 'Payments',
  })

  const { data: notifData } = useQuery({
    queryKey: ['shop-notifications', id],
    queryFn: () => shopsApi.getNotifications(id!).then((r) => r.data),
    enabled: !!id && activeTab === 'Notifications',
  })

  const { data: ticketsData } = useQuery({
    queryKey: ['shop-tickets', id],
    queryFn: () => shopsApi.getTickets(id!).then((r) => r.data),
    enabled: !!id && activeTab === 'Support Tickets',
  })

  const { data: auditData } = useQuery({
    queryKey: ['shop-audit', id],
    queryFn: () => shopsApi.getAuditLogs(id!).then((r) => r.data),
    enabled: !!id && activeTab === 'Audit Logs',
  })

  // Sync form when shop data loads
  useEffect(() => {
    if (shopData) {
      setForm({
        businessTypeId: shopData.businessTypeId ?? '',
        logoUrl: shopData.logoUrl ?? '',
        timezoneOverride: shopData.timezoneOverride ?? '',
        internalNote: shopData.internalNote ?? '',
      })
    }
  }, [shopData])

  const refreshShop = () => {
    queryClient.invalidateQueries({ queryKey: ['shop', id] })
    queryClient.invalidateQueries({ queryKey: ['shops'] })
  }

  const subMutation = useMutation({
    mutationFn: () => {
      const subId = shopData?.subscription?.id
      if (!subId) throw new Error('No subscription')
      return subAction === 'extend'
        ? subscriptionsApi.extend(subId, extendDays, subReason.trim())
        : subscriptionsApi.changePlan(subId, newPlanId, subReason.trim())
    },
    onSuccess: () => {
      refreshShop()
      showToast({ type: 'success', message: subAction === 'extend' ? tx('Subscription extended.') : tx('Plan changed.') })
      setSubAction(null)
      setSubReason('')
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, tx('Action failed.')) }),
  })

  const suspendMutation = useMutation({
    mutationFn: () => shopsApi.suspend(id!, reason),
    onSuccess: () => {
      refreshShop()
      showToast({ type: 'success', message: tx('Shop suspended.') })
      setSuspendDialog(false)
      setReason('')
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, tx('Failed to suspend shop.')) }),
  })

  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) => shopsApi.sendUserPasswordReset(id!, userId),
    onSuccess: (r) => showToast({ type: 'success', message: `ส่งลิงก์ตั้งรหัสผ่านใหม่ไปที่ ${r.data.data?.email ?? 'อีเมลผู้ใช้'} แล้ว (ใช้ได้ 1 ชม.)` }),
    onError: (e) => showToast({ type: 'error', message: errorOf(e, 'ส่งลิงก์รีเซ็ตไม่สำเร็จ') }),
  })

  const verifyEmailMutation = useMutation({
    mutationFn: (userId: string) => shopsApi.verifyUserEmail(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-users', id] })
      showToast({ type: 'success', message: 'ยืนยันอีเมลแล้ว — ผู้ใช้เข้าสู่ระบบได้เลย' })
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, 'ยืนยันอีเมลไม่สำเร็จ') }),
  })

  const reactivateMutation = useMutation({
    mutationFn: () => shopsApi.reactivate(id!, reason),
    onSuccess: () => {
      refreshShop()
      showToast({ type: 'success', message: tx('Shop reactivated.') })
      setReactivateDialog(false)
      setReason('')
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, tx('Failed to reactivate shop.')) }),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof shopsApi.update>[1]) =>
      shopsApi.update(id!, data),
    onSuccess: () => {
      // Stay on the page — the refetch re-fills the form with what was saved.
      refreshShop()
      showToast({ type: 'success', message: tx('Admin settings saved.') })
    },
    onError: (e) => {
      showToast({ type: 'error', message: errorOf(e, 'บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง') })
    },
  })

  const handleSave = () => {
    updateMutation.mutate({
      businessTypeId: form.businessTypeId || null,
      logoUrl: form.logoUrl.trim() || null,
      timezoneOverride: form.timezoneOverride.trim() || null,
      internalNote: form.internalNote.trim() || null,
    })
  }

  const invoices = invoicesData?.data ?? []
  const auditLogs = auditData?.data ?? []
  const payments = (paymentsData?.data ?? []) as CorePayment[]
  const notifications = (notifData?.data ?? []) as unknown as NotificationLog[]
  const tickets = (ticketsData?.data ?? []) as unknown as SupportTicket[]

  if (isLoading) return <LoadingState variant="spinner" message={tx('Loading shop...')} />
  if (!shopData) return <ErrorState message={tx('Failed to load shop.')} onRetry={() => refetch()} />
  const shop = shopData
  const usage = usageData ?? { bookingsCount: 0, posBillsCount: 0, staffCount: 0, branchesCount: 0, customersCount: 0 }
  void error
  const users = (usersData ?? []) as ShopUser[]
  const branches = (branchesData ?? []) as ShopBranch[]
  const sub = shop.subscription ?? null
  const daysLeft = sub?.nextBillingDate ? Math.ceil((new Date(sub.nextBillingDate).getTime() - Date.now()) / 86_400_000) : null
  const dirty =
    form.businessTypeId !== (shop.businessTypeId ?? '') ||
    form.logoUrl !== (shop.logoUrl ?? '') ||
    form.timezoneOverride !== (shop.timezoneOverride ?? '') ||
    form.internalNote !== (shop.internalNote ?? '')

  const userColumns: Column<ShopUser>[] = [
    {
      key: 'name',
      header: tx('Name'),
      render: (u) => (
        <div>
          <p className="font-medium text-gray-900">{u.name}</p>
          <p className="text-xs text-gray-400">{u.email}</p>
        </div>
      ),
    },
    { key: 'role', header: tx('Role'), render: (u) => <span className="text-sm">{tx(ROLE_LABEL[u.role] ?? u.role)}</span> },
    { key: 'authProvider', header: tx('Sign-in'), render: (u) => <span className="text-xs text-gray-500">{u.authProvider ?? 'password'}</span> },
    {
      key: 'active',
      header: tx('Status'),
      render: (u) => <StatusBadge status={u.active ? 'active' : 'suspended'} label={u.active ? tx('Active') : tx('Disabled')} />,
    },
    {
      key: 'lastLoginAt',
      header: tx('Last login'),
      render: (u) => (
        <span className="text-sm text-gray-600" title={formatDatetime(u.lastLoginAt)}>
          {u.lastLoginAt ? formatRelativeTime(u.lastLoginAt) : tx('Never')}
        </span>
      ),
    },
    {
      key: 'emailVerifiedAt',
      header: 'อีเมล',
      render: (u) =>
        u.emailVerifiedAt || (u.authProvider && u.authProvider !== 'password') ? (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700" data-testid="user-email-verified">ยืนยันแล้ว</span>
        ) : can('shops.update') ? (
          <button
            onClick={() => verifyEmailMutation.mutate(u.id)}
            disabled={verifyEmailMutation.isPending}
            className="text-xs font-medium px-2.5 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            data-testid="user-verify-email"
            title="ผู้ใช้ยังไม่ได้กดลิงก์ยืนยันในอีเมล — กดเพื่อยืนยันแทน"
          >
            ยืนยันอีเมลให้
          </button>
        ) : (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">ยังไม่ยืนยัน</span>
        ),
    },
    {
      key: 'passwordReset',
      header: 'รหัสผ่าน',
      render: (u) =>
        u.authProvider && u.authProvider !== 'password' ? (
          <span className="text-xs text-gray-400">{u.authProvider}</span>
        ) : can('shops.update') ? (
          <button
            onClick={() => {
              if (window.confirm(`ส่งลิงก์ตั้งรหัสผ่านใหม่ไปที่ ${u.email} ?`)) resetPasswordMutation.mutate(u.id)
            }}
            disabled={resetPasswordMutation.isPending}
            className="text-xs font-medium px-2.5 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-testid="user-send-password-reset"
            title="ส่งอีเมลลิงก์ตั้งรหัสผ่านใหม่ให้ผู้ใช้ — ทีมงานไม่เห็นและไม่ได้ตั้งรหัสผ่านเอง"
          >
            ส่งลิงก์รีเซ็ต
          </button>
        ) : null,
    },
    { key: 'createdAt', header: tx('Created'), render: (u) => <span className="text-sm text-gray-500">{formatDate(u.createdAt)}</span> },
  ]

  const branchColumns: Column<ShopBranch>[] = [
    {
      key: 'name',
      header: tx('Branch'),
      render: (b) => (
        <div>
          <p className="font-medium text-gray-900">
            {b.name} {b.isMain && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{tx('Main')}</span>}
          </p>
          <p className="text-xs text-gray-400">{b.address ?? b.phone ?? '—'}</p>
        </div>
      ),
    },
    { key: 'hours', header: tx('Hours'), render: (b) => <span className="text-sm">{b.openTime && b.closeTime ? `${b.openTime}–${b.closeTime}` : '—'}</span> },
    { key: 'staffCount', header: tx('Staff Members'), render: (b) => b.staffCount },
    { key: 'bookings30d', header: tx('Bookings (30 days)'), render: (b) => b.bookings30d },
    { key: 'liffEnabled', header: tx('Online booking'), render: (b) => (b.liffEnabled ? tx('On') : tx('Off')) },
    { key: 'status', header: tx('Status'), render: (b) => <StatusBadge status={b.status} /> },
  ]

  const paymentColumns: Column<CorePayment>[] = [
    { key: 'createdAt', header: tx('Date'), render: (r) => formatDatetime(r.verifiedAt ?? r.createdAt) },
    { key: 'kind', header: tx('Item'), render: (r) => (r.kind === 'addon' ? `Add-on ${r.addonCode}` : `${r.planCode} · ${r.billingCycle}`) },
    { key: 'amountBaht', header: tx('Amount'), render: (r) => formatMoney(r.amountBaht) },
    { key: 'status', header: tx('Status'), render: (r) => <StatusBadge status={r.status === 'verified' ? 'paid' : r.status} label={r.status} /> },
    {
      key: 'slip',
      header: tx('Slip'),
      render: (r) =>
        r.slipImageUrl ? (
          <a href={r.slipImageUrl} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs hover:underline">
            ดูสลิป
          </a>
        ) : (
          '—'
        ),
    },
  ]

  const notificationColumns: Column<NotificationLog>[] = [
    { key: 'createdAt', header: tx('Date'), render: (r) => formatDatetime(r.createdAt) },
    { key: 'channel', header: tx('Channel'), render: (r) => r.channel },
    { key: 'title', header: tx('Title'), render: (r) => r.title ?? '—' },
    { key: 'status', header: tx('Status'), render: (r) => r.status },
  ]

  const ticketColumns: Column<SupportTicket>[] = [
    { key: 'createdAt', header: tx('Date'), render: (r) => formatDatetime(r.createdAt) },
    { key: 'subject', header: tx('Subject'), render: (r) => r.subject },
    { key: 'priority', header: tx('Priority'), render: (r) => r.priority },
    { key: 'status', header: tx('Status'), render: (r) => <StatusBadge status={r.status} /> },
  ]

  const invoiceColumns: Column<BillingInvoice>[] = [
    { key: 'invoiceNumber', header: tx('Invoice #') },
    { key: 'amount', header: tx('Amount'), render: (r) => formatMoney(r.amount, r.currency) },
    { key: 'status', header: tx('Status'), render: (r) => <StatusBadge status={r.status} /> },
    { key: 'dueDate', header: tx('Due Date'), render: (r) => formatDate(r.dueDate) },
    { key: 'paidAt', header: tx('Paid At'), render: (r) => formatDate(r.paidAt) },
  ]

  const auditColumns: Column<AuditLog>[] = [
    { key: 'createdAt', header: tx('Date'), render: (r) => formatDatetime(r.createdAt) },
    { key: 'actorName', header: tx('Admin'), render: (r) => r.actorName ?? 'System' },
    { key: 'action', header: tx('Action'), render: (r) => <span className="font-mono text-xs text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">{r.action}</span> },
    { key: 'targetType', header: tx('Target'), render: (r) => r.targetType ?? '—' },
  ]

  return (
    <div className="space-y-5">
      {/* Back + header */}
      <div>
        <button
          onClick={() => navigate('/shops')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {tx('Back to Shops')}
        </button>
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">{shop.name}</h1>
                <StatusBadge status={shop.status} />
              </div>
              <p className="text-sm text-gray-500">
                {shop.ownerName ? `${shop.ownerName} · ` : ''}
                {shop.ownerEmail} · <PlanBadge plan={shop.currentPlan} size="sm" />
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {can('shops.suspend') && (shop.status === 'suspended' ? (
              <button
                onClick={() => { setReactivateDialog(true); setReason('') }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <ShieldCheck className="h-4 w-4" />
                {tx('Reactivate')}
              </button>
            ) : (
              <button
                onClick={() => { setSuspendDialog(true); setReason('') }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <ShieldOff className="h-4 w-4" />
                {tx('Suspend')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab}
            data-testid={`tab-${tab.toLowerCase().replace(/ /g, '-')}`}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${
              activeTab === tab
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tx(tab)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'Overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Shop Information — read-only, owned by queza-core */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">{tx('Shop Information')}</h3>
                <p className="text-xs text-gray-400 mb-3">{tx('Managed in queza-core — view only here')}</p>
                <div>
                  <InfoRow label={tx('Shop Name')} value={shop.name} icon={Building2} />
                  <InfoRow label={tx('Address')} value={shop.address} icon={MapPin} />
                  <InfoRow label={tx('Created')} value={formatDatetime(shop.createdAt)} icon={Calendar} />
                  <InfoRow label={tx('Plan')} value={<PlanBadge plan={shop.currentPlan} />} />
                  <InfoRow label={tx('Status')} value={<StatusBadge status={shop.status} />} />
                  <InfoRow label={tx('Business Type')} value={shop.businessTypeName} />
                </div>
              </div>

              {/* Owner Information — read-only, owned by queza-core */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">{tx('Owner Information')}</h3>
                <p className="text-xs text-gray-400 mb-3">{tx('Managed in queza-core — view only here')}</p>
                <div>
                  <InfoRow label={tx('Name')} value={shop.ownerName} icon={Users} />
                  <InfoRow label="Email" value={shop.ownerEmail} icon={Mail} />
                  <InfoRow label={tx('Phone')} value={shop.ownerPhone} icon={Phone} />
                  <InfoRow
                    label={tx('Last login')}
                    value={shop.ownerLastLoginAt ? `${formatDatetime(shop.ownerLastLoginAt)} (${formatRelativeTime(shop.ownerLastLoginAt)})` : tx('Never')}
                    icon={Clock}
                  />
                </div>
              </div>

              <SetupCard shop={shop} usersCount={users.length} />
              <HealthCard shop={shop} />

              {/* Admin Settings — editable, this console owns these fields */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 md:col-span-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">{tx('Admin Settings')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">ประเภทธุรกิจ</label>
                    <select
                      value={form.businessTypeId}
                      onChange={(e) => setForm((f) => ({ ...f, businessTypeId: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">-- ไม่ระบุ --</option>
                      {(businessTypesData ?? []).map((bt) => (
                        <option key={bt.id} value={bt.id}>{bt.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Logo URL</label>
                    <input
                      type="text"
                      value={form.logoUrl}
                      onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{tx('Timezone Override')}</label>
                    <input
                      type="text"
                      value={form.timezoneOverride}
                      onChange={(e) => setForm((f) => ({ ...f, timezoneOverride: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Asia/Bangkok"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">{tx('Internal Note')}</label>
                    <textarea
                      value={form.internalNote}
                      onChange={(e) => setForm((f) => ({ ...f, internalNote: e.target.value }))}
                      rows={3}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="หมายเหตุสำหรับทีม admin เท่านั้น"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Save/Cancel buttons */}
            {can('shops.update') && (
            <div className="flex justify-end gap-3 mt-2 pt-4 border-t border-gray-100">
              <button
                onClick={() =>
                  setForm({
                    businessTypeId: shop.businessTypeId ?? '',
                    logoUrl: shop.logoUrl ?? '',
                    timezoneOverride: shop.timezoneOverride ?? '',
                    internalNote: shop.internalNote ?? '',
                  })
                }
                disabled={!dirty || updateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                {tx('Discard changes')}
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending || !dirty}
                data-testid="shop-admin-save"
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="h-4 w-4" />
                {updateMutation.isPending ? tx('Saving…') : tx('Save')}
              </button>
            </div>
            )}
          </div>
        )}

        {activeTab === 'Subscription' && (
          <div className="space-y-5">
            {!sub ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-sm text-gray-500">
                {tx('This shop has no subscription record in queza-core.')}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700">{tx('Subscription')}</h3>
                    <p className="text-xs text-gray-400">{tx('Live from queza-core')}</p>
                  </div>
                  <div className="flex gap-2">
                    {can('subscriptions.extend') && (
                    <button
                      onClick={() => { setSubAction('extend'); setSubReason('') }}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <CalendarPlus className="h-4 w-4" /> {tx('Extend')}
                    </button>
                    )}
                    {can('subscriptions.change_plan') && (
                    <button
                      onClick={() => { setSubAction('plan'); setSubReason(''); setNewPlanId('') }}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <Repeat className="h-4 w-4" /> {tx('Change Plan')}
                    </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Stat label={tx('Plan')} value={<PlanBadge plan={sub.planName ?? sub.planCode ?? '—'} />} />
                  <Stat label={tx('Status')} value={<StatusBadge status={sub.status ?? 'gray'} />} />
                  <Stat label={tx('Billing cycle')} value={sub.billingCycle === 'yearly' ? tx('Yearly') : tx('Monthly')} />
                  <Stat
                    label={sub.status === 'trial' ? tx('Trial ends') : tx('Next billing')}
                    value={
                      <span>
                        {formatDate(sub.nextBillingDate)}
                        {daysLeft !== null && (
                          <span className={`block text-xs ${daysLeft < 0 ? 'text-red-600' : daysLeft <= 7 ? 'text-amber-600' : 'text-gray-400'}`}>
                            {daysLeft < 0 ? `${tx('Overdue')} ${-daysLeft} ${tx('days')}` : `${tx('in')} ${daysLeft} ${tx('days')}`}
                          </span>
                        )}
                      </span>
                    }
                  />
                  <Stat
                    label={tx('Locked price')}
                    value={
                      sub.lockedMonthlyBaht != null || sub.lockedYearlyBaht != null
                        ? [
                            sub.lockedMonthlyBaht != null ? `${formatMoney(sub.lockedMonthlyBaht)}/${tx('mo')}` : null,
                            sub.lockedYearlyBaht != null ? `${formatMoney(sub.lockedYearlyBaht)}/${tx('yr')}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        : tx('Current list price')
                    }
                  />
                  <div className="col-span-2 md:col-span-3">
                    <Stat
                      label={tx('AI Add-ons')}
                      value={
                        <div className="flex flex-wrap items-center gap-1.5">
                          {sub.addons.length ? (
                            sub.addons.map((a) => (
                              <span key={a} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700"><Bot className="w-3 h-3" />{AI_ADDON_NAMES[a] ?? a}</span>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">{tx('None')}</span>
                          )}
                          {can('subscriptions.addons') && (
                            <button
                              onClick={() => navigate(`/subscriptions?search=${encodeURIComponent(shop.name)}`)}
                              className="text-xs text-purple-600 hover:underline ml-1"
                            >
                              {sub.addons.length ? tx('Manage') : tx('Grant AI add-on')}
                            </button>
                          )}
                        </div>
                      }
                    />
                  </div>
                </div>

                {subAction && (
                  <div className="mt-5 pt-4 border-t border-gray-100 grid md:grid-cols-3 gap-3 items-end">
                    {subAction === 'extend' ? (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{tx('Extend by (days)')}</label>
                        <select className={inputCls} value={extendDays} onChange={(e) => setExtendDays(Number(e.target.value))}>
                          {[7, 14, 30, 60, 90, 365].map((d) => (
                            <option key={d} value={d}>{d} {tx('days')}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{tx('New plan')}</label>
                        <select className={inputCls} value={newPlanId} onChange={(e) => setNewPlanId(e.target.value)}>
                          <option value="">{tx('Select plan')}</option>
                          {(plansData ?? [])
                            .filter((p) => p.isActive && p.name !== sub.planCode)
                            .map((p) => (
                              <option key={p.id} value={p.id}>{p.displayName || p.name}</option>
                            ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{tx('Reason')} *</label>
                      <input className={inputCls} value={subReason} onChange={(e) => setSubReason(e.target.value)} placeholder={tx('Please provide a reason...')} />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => subMutation.mutate()}
                        disabled={subMutation.isPending || !subReason.trim() || (subAction === 'plan' && !newPlanId)}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {subMutation.isPending ? tx('Saving…') : tx('Confirm')}
                      </button>
                      <button onClick={() => setSubAction(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                        {tx('Cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'Users' && (
          <DataTable columns={userColumns} data={users} isLoading={usersLoading} emptyMessage={tx('No users.')} />
        )}

        {activeTab === 'Branches' && (
          <DataTable columns={branchColumns} data={branches} isLoading={branchesLoading} emptyMessage={tx('No branches.')} />
        )}

        {activeTab === 'Usage' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: tx('Bookings'), value: usage.bookingsCount.toLocaleString(), icon: Calendar },
              { label: tx('POS Bills'), value: usage.posBillsCount.toLocaleString(), icon: BarChart2 },
              { label: tx('Staff Members'), value: usage.staffCount.toLocaleString(), icon: Users },
              { label: tx('Branches'), value: usage.branchesCount.toLocaleString(), icon: Building2 },
              { label: tx('Customers'), value: usage.customersCount.toLocaleString(), icon: Users },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-indigo-600" />
                  <p className="text-sm text-gray-500">{label}</p>
                </div>
                <p className="text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'Billing Logs' && (
          <DataTable
            columns={invoiceColumns}
            data={invoices}
            emptyMessage={tx('No billing records found.')}
          />
        )}

        {activeTab === 'Audit Logs' && (
          <DataTable
            columns={auditColumns}
            data={auditLogs}
            emptyMessage={tx('No audit logs found.')}
          />
        )}

        {activeTab === 'Payments' && (
          <DataTable columns={paymentColumns} data={payments} emptyMessage="ร้านนี้ยังไม่มีการชำระค่าแพ็กเกจ" />
        )}

        {activeTab === 'Notifications' && (
          <DataTable columns={notificationColumns} data={notifications} emptyMessage={tx('No notifications sent to this shop.')} />
        )}

        {activeTab === 'Support Tickets' && (
          <DataTable columns={ticketColumns} data={tickets} emptyMessage={tx('No support tickets.')} />
        )}
      </div>

      <ConfirmDialog
        open={suspendDialog}
        onClose={() => setSuspendDialog(false)}
        onConfirm={() => suspendMutation.mutate()}
        title={`${tx('Suspend')} "${shop.name}"`}
        description={tx("This will block the shop owner's access immediately.")}
        confirmLabel={tx('Suspend')}
        variant="danger"
        requireReason
        reason={reason}
        onReasonChange={setReason}
        isLoading={suspendMutation.isPending}
      />

      <ConfirmDialog
        open={reactivateDialog}
        onClose={() => setReactivateDialog(false)}
        onConfirm={() => reactivateMutation.mutate()}
        title={`${tx('Reactivate')} "${shop.name}"`}
        description={tx("This will restore the shop's platform access.")}
        confirmLabel={tx('Reactivate')}
        variant="default"
        requireReason
        reason={reason}
        onReasonChange={setReason}
        isLoading={reactivateMutation.isPending}
      />
    </div>
  )
}

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <div className="text-sm font-medium text-gray-800">{value ?? '—'}</div>
  </div>
)

const Check: React.FC<{ ok: boolean; label: string; hint?: string }> = ({ ok, label, hint }) => (
  <div className="flex items-start gap-2.5 py-2">
    {ok ? (
      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
    ) : (
      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
    )}
    <div>
      <p className={`text-sm ${ok ? 'text-gray-700' : 'text-amber-700'}`}>{label}</p>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  </div>
)

/** Setup checklist — what the shop has / hasn't configured in queza-core. */
const SetupCard: React.FC<{ shop: Shop; usersCount: number }> = ({ shop, usersCount }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
      <MessageCircle className="h-4 w-4 text-indigo-600" /> {tx('Setup status')}
    </h3>
    <Check ok={!!shop.setup?.onboardingDone} label={shop.setup?.onboardingDone ? tx('Onboarding done') : tx('Onboarding not finished')} />
    <Check
      ok={!!shop.setup?.lineConnected}
      label={shop.setup?.lineConnected ? tx('LINE OA connected') : tx('LINE OA not connected')}
      hint={shop.setup?.lineConnected ? undefined : tx('Customers cannot book via LINE yet')}
    />
    <Check ok={usersCount > 1} label={`${tx('Users')}: ${usersCount}`} hint={usersCount <= 1 ? tx('Only the owner account so far') : undefined} />
  </div>
)

/** Churn signals — recent bookings and last activity. */
const HealthCard: React.FC<{ shop: Shop }> = ({ shop }) => {
  const b = shop.bookings30d ?? 0
  const idleDays = shop.lastActivityAt ? Math.floor((Date.now() - new Date(shop.lastActivityAt).getTime()) / 86_400_000) : null
  const risk = b === 0 || idleDays === null || idleDays > 14
  return (
    <div className={`rounded-xl border shadow-sm p-5 ${risk ? 'bg-amber-50/50 border-amber-200' : 'bg-white border-gray-200'}`}>
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <BarChart2 className="h-4 w-4 text-indigo-600" /> {tx('Engagement')}
        {risk && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{tx('Churn risk')}</span>}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <Stat label={tx('Bookings (30 days)')} value={<span className="text-2xl font-bold tabular-nums">{b}</span>} />
        <Stat
          label={tx('Last activity')}
          value={
            <span title={formatDatetime(shop.lastActivityAt)}>{shop.lastActivityAt ? formatRelativeTime(shop.lastActivityAt) : tx('Never')}</span>
          }
        />
        <Stat label={tx('Last booking')} value={formatDatetime(shop.lastBookingAt)} />
        <Stat label={tx('Last login (any user)')} value={formatDatetime(shop.lastLoginAt)} />
      </div>
    </div>
  )
}

export default ShopDetailPage
